export type DiligenceUploadTarget = 'template' | 'attachment';

const SUPPORTED_EXTENSIONS = new Set(['pdf', 'doc', 'docx']);
const SPREADSHEET_EXTENSIONS = new Set(['xls', 'xlsx', 'ods']);

export function getUnsupportedDiligenceFormatMessage(fileName: string): string | null {
  const extension = fileName.match(/\.([^.]+)$/)?.[1]?.toLowerCase();
  if (extension && SUPPORTED_EXTENSIONS.has(extension)) return null;
  if (extension && SPREADSHEET_EXTENSIONS.has(extension)) {
    return 'Juris no admite hojas de cálculo. Exporta la hoja como PDF y vuelve a intentarlo.';
  }
  if (extension && ['docm', 'odt', 'rtf'].includes(extension)) {
    return 'Guarda el documento Word como DOCX y vuelve a intentarlo.';
  }
  return 'Juris admite PDF y documentos Word DOC o DOCX. Exporta las hojas de cálculo como PDF.';
}

export function getDiligenceUploadErrorMessage(
  target: DiligenceUploadTarget,
  code: unknown
) {
  const noun = target === 'template' ? 'archivo' : 'documento';
  const action = target === 'template' ? 'subirlo' : 'adjuntarlo';
  if (code === 'unsupported_document') {
    return `Este ${noun} contiene elementos que Juris no puede procesar con seguridad. Guarda una copia exportada sin esos elementos y vuelve a ${action}.`;
  }
  if (code === 'invalid_upload') {
    return `El ${noun} no se pudo validar. Comprueba que no esté dañado y vuelve a ${action}.`;
  }
  if (code === 'pdf_content_credentials_not_supported') {
    return `Este PDF contiene credenciales de contenido que Juris no puede conservar al editar. Exporta una copia sin esas credenciales y vuelve a ${action}.`;
  }
  if (code === 'pdf_signature_not_supported') {
    return `Este PDF tiene una firma o certificación que protege el original. Para editarlo, adjunta una copia autorizada exportada sin esa protección.`;
  }
  if (code === 'pdf_encrypted_not_supported') {
    return `Este PDF está protegido con contraseña y Juris no puede procesarlo así. Ábrelo tú y adjunta una copia sin protección; no compartas la contraseña.`;
  }
  if (code === 'pdf_embedded_files_not_supported') {
    return `Este PDF contiene archivos adjuntos que Juris no puede procesar. Exporta una copia sin esos archivos y vuelve a ${action}.`;
  }
  if (code === 'pdf_active_content_not_supported') {
    return `Este PDF contiene contenido activo que Juris no admite. Guarda una copia estática sin ese contenido y vuelve a ${action}.`;
  }
  if (code === 'pdf_viewer_preferences_not_supported') {
    return `Este PDF contiene preferencias de visualización no admitidas. Ábrelo y expórtalo como un PDF nuevo antes de ${action}.`;
  }
  if (code === 'unsupported_file_format') {
    return 'Juris admite PDF y documentos Word DOC o DOCX. Exporta las hojas de cálculo como PDF.';
  }
  return target === 'template'
    ? 'No se pudo completar la operación con la plantilla.'
    : 'No se pudo registrar el documento adjunto.';
}
